import {
  computed,
  defineComponent,
  reactive,
  watch,
} from '@vue/composition-api';
import TableFilter from '@/components/common/table-filter.component.vue'; // フィルタ用共通コンポーネント
import { SchoolHeader, TableSchool } from '@/shared/type';
import { useTable } from '@/hooks/useTable';
import { ResponseSchool } from '@/shared/types/entities/School';
import { toTableSchools } from '@/utils/transform';
import { statusFilterFactory, StatusListFilter } from '@/constants/initialData';
import Reset from '../reset.component.vue';
import { endPoints } from '@/shared/types/api';
import { axios } from '@/plugins/axios';
import { DatePicker, Typography } from '@/components/parts';
import {
  tableFooterProps,
  tableHeaderClass,
} from '@/constants/masterData/v-data-table';
import { download } from '@/utils/fileDownload';

const schoolHeaders: SchoolHeader[] = [
  {
    text: '',
    value: 'data-table-select',
    width: '1px',
    class: tableHeaderClass,
  },
  {
    text: '学校名称',
    value: 'name',
    width: '23%',
    class: tableHeaderClass,
  },
  {
    text: 'リハーサル用',
    value: 'rehearsalDate',
    width: '30%',
    class: tableHeaderClass,
  },
  {
    text: '本番用',
    value: 'productionDate',
    width: '30%',
    class: tableHeaderClass,
  },
  {
    text: '周知用',
    value: 'information',
    width: '15%',
    class: tableHeaderClass,
    sortable: false,
  },
];

// リアクティブな変数の型定義
export type ReactiveData = {
  statusListItem: TableSchool[]; // 学校一覧情報
  total: number; // 全件数
  selectedItems: TableSchool[];
  isOpenDialog: boolean;
  startDate: string; // 一括変更
  endDate: string;
  publishType: number | undefined;
  editTargets: number[];
  isLoading: boolean;
};

type Props = {
  organizationId: number;
};

export default defineComponent({
  components: { TableFilter, Reset, DatePicker, Typography },
  setup(_, ctx) {
    const props = {
      organizationId: ctx.attrs['organization-id'],
    } as Props;

    const state: ReactiveData = reactive<ReactiveData>({
      statusListItem: [],
      total: 0, // 全件数
      selectedItems: [],
      isOpenDialog: false,
      startDate: '',
      endDate: '',
      publishType: undefined,
      editTargets: [],
      isLoading: false,
    });

    const {
      fetchTableData,
      downloadList,
      downloadPDF,
      pagination,
      tableOptions,
      updateOptions,
      tableState,
    } = useTable<TableSchool, ResponseSchool, StatusListFilter>({
      fetch: async (query: { [k: string]: unknown }) => {
        return (
          await axios.get(endPoints.listStatuses.endPoint, {
            params: {
              ...query,
              organization_type_id: props.organizationId,
            },
          })
        ).data;
      },
      download: async (query: { [k: string]: unknown }) => {
        await axios
          .get(endPoints.downloadStatusesList.endPoint, {
            params: {
              ...query,
              organization_type_id: props.organizationId,
              page: 1,
              items_per_page: 10,
            },
            responseType: 'blob',
          })
          .then(download);
      },
      initialFilterFactory: statusFilterFactory,
    });

    const handleFetch = async () => {
      state.isLoading = true;
      const res = await fetchTableData(tableState.filter);

      const parsed = toTableSchools(res?.items || []);

      state.statusListItem = parsed;

      state.total = res?.total || 0;

      state.isLoading = false;
    };

    watch(tableOptions, handleFetch);

    const handleSelect = (items: TableSchool[]) => {
      const isRemoveAll =
        tableState.selected.length - pagination.itemsPerPage === items.length;

      if (isRemoveAll) {
        tableState.selected = [];
      } else if (items.length === pagination.itemsPerPage) {
        tableState.selected = state.statusListItem;
      } else {
        tableState.selected = items;
      }
    };

    const removeFilterItem = (key: string) => {
      const itemKey = key as keyof StatusListFilter;
      tableState.filter[itemKey].value = undefined;
      if (tableState.filter[itemKey].rangeProps) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        tableState.filter[itemKey].rangeProps!.value = undefined;
      }
      handleFetch();
    };

    const setFilter = (filter: StatusListFilter) => {
      tableState.filter = filter;
      handleFetch();
    };

    const selectedIds = computed(() =>
      tableState.selected.map((item) => item.id),
    );

    const statusListItem = computed(() => {
      const index = (pagination.page - 1) * pagination.itemsPerPage;
      return state.statusListItem.slice(index, index + pagination.itemsPerPage);
    });

    return {
      ...props,
      statusListItem,
      schoolHeaders,
      state,
      tableState,
      pagination,
      tableOptions,
      handleSelect,
      updateOptions,
      setFilter,
      removeFilterItem,
      handleFetch,
      downloadList: () => downloadList(tableState.filter),
      downloadPDF,
      selectedIds,
      footerProps: tableFooterProps,
    };
  },
});
